import React from 'react';
import OptionalLabel from '../../../../../../components/envago/OptionalLabel/OptionalLabel';
import ReadingValue from '../../../../../../components/envago/ReadingValue/ReadingValue';
import MeterReadingUnit from '../../../../../../components/envago/MeterReadingUnit/MeterReadingUnit';
import MeterReadingPlausibilityIndicator from '../../../../../../components/envago/MeterReadingPlausibilityIndicator';
import { resolvePlausibilityIndicator } from '../../../../../../helper/PlausibilityHelper';

interface ReadingValueLabelProps {
    label: string;
    value?: number;
    preDecimals?: number;
    postDecimals?: number;
    maskPostDecimals?: boolean;
    unit?: string;
    obis?: string;
    minimumValue?: number;
    maximumValue?: number;
    /** config.features.showPlausibilityAsText: hint as text below the value instead of the arrow. */
    plausibilityAsText?: boolean;
}

/**
 * The "Ablesewert" row of a recorded reading (done view and success screen).
 *
 * The framed reading value is taller than a plain text row. In the two-column layout (lg) the
 * value is therefore pulled into the row gap (-my-1) and the label is centered on it, so the
 * label spacing matches the other rows. With a hint text below the value the label stays on the
 * value line instead, and the hint keeps its distance to the frame.
 */
const ReadingValueLabel = ({
    label,
    value,
    preDecimals,
    postDecimals,
    maskPostDecimals,
    unit,
    obis,
    minimumValue,
    maximumValue,
    plausibilityAsText,
}: ReadingValueLabelProps) => {
    const showsHintText = !!plausibilityAsText && !!resolvePlausibilityIndicator(value, minimumValue, maximumValue);

    return (
        <OptionalLabel
            label={label}
            className={'flex flex-row items-center lg:-my-1'}
            labelClassName={showsHintText ? undefined : 'lg:self-center'}
            description={
                showsHintText && (
                    <MeterReadingPlausibilityIndicator
                        asText
                        className={'block mt-1 lg:mt-2'}
                        readingValue={value}
                        minimumValue={minimumValue}
                        maximumValue={maximumValue}
                    />
                )
            }
        >
            <ReadingValue value={value} postDecimals={postDecimals} preDecimals={preDecimals} maskPostDecimals={maskPostDecimals} />
            <MeterReadingUnit className={'text-gray-600'} unit={unit} obis={obis} />
            {!plausibilityAsText && (
                <MeterReadingPlausibilityIndicator readingValue={value} minimumValue={minimumValue} maximumValue={maximumValue} />
            )}
        </OptionalLabel>
    );
};

export default ReadingValueLabel;
